// NameVariantsCalculator.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const NameVariantsCalculator = () => {
  const [name, setName] = useState('');
  const [variants, setVariants] = useState([]);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateVariants = () => {
    if (!name) {
      setError('Please enter a name');
      return;
    }

    // Mock name variants calculation logic
    const nameVariants = [
      name.toLowerCase(),
      name.toUpperCase(),
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
      name.split('').reverse().join(''),
      name.split('').map((char, index) => index % 2 === 0 ? char.toUpperCase() : char.toLowerCase()).join('')
    ];

    setVariants(nameVariants);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Name Variants Calculator | Generate Creative Name Variations</title>
        <meta name="description" content="Generate creative name variations and transformations with our Name Variants Calculator. Explore different ways to write and stylize names." />
        <meta name="keywords" content="name variants, name variations, name transformations, name styling, creative name generator" />
        <meta property="og:title" content="Name Variants Calculator | Generate Creative Name Variations" />
        <meta property="og:description" content="Generate creative name variations and transformations with our Name Variants Calculator. Explore different ways to write and stylize names." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/name-variants-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">📝</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Name Variants Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate various name variants</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator generates various name variants for a given name.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Name:
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a name"
          />
        </div>

        <button
          onClick={calculateVariants}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Variants
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {variants.length > 0 && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-4">Name Variants</h4>
              <ul className="list-disc pl-8 space-y-2">
                {variants.map((variant, index) => (
                  <li key={index} className="text-gray-700">{variant}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Name Variants Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Name Variants Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the name in the "Name" field.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Variants</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Variants" button.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the list of generated name variants.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Full FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the Name Variants Calculator?</h3>
              <p className="text-gray-700">
                The Name Variants Calculator is a tool that generates various name variants for a given name.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the calculator work?</h3>
              <p className="text-gray-700">
                The calculator uses a simple algorithm to generate different variants of the input name.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Is the list of variants comprehensive?</h3>
              <p className="text-gray-700">
                No, the list of variants is a mock representation for demonstration purposes only. It is not exhaustive.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this tool for any names?</h3>
              <p className="text-gray-700">
                Yes, you can use this tool for any names. However, the results are for fun and should not be taken seriously.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Instant Results</h3>
              <p className="text-gray-700">
                Get immediate name variants for any given name.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">User-Friendly Interface</h3>
              <p className="text-gray-700">
                Easy-to-use interface with clear instructions and intuitive design.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Fun and Engaging</h3>
              <p className="text-gray-700">
                A fun way to see different variants of a name, perfect for entertainment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameVariantsCalculator;